import React, { useState } from 'react';
import './table.css';
import { Icon } from 'components/ui/icon';
import {
  Table as BaseTable,
  Header,
  HeaderRow,
  Body,
  Row,
  Cell,
  HeaderCell,
  Footer,
  FooterRow,
  FooterCell,
} from '@table-library/react-table-library/table';
import {
  HeaderCellSort,
  useSort,
  SortFn,
} from '@table-library/react-table-library/sort';
import { Theme } from '@table-library/react-table-library/types/theme';
import {
  CellSelect,
  HeaderCellSelect,
  useRowSelect,
  SelectClickTypes,
} from '@table-library/react-table-library/select';
import { useTheme } from '@table-library/react-table-library/theme';
import NoDataAvailable from 'components/empty-state';
import classNames from 'classnames';

export interface ColumnConfig<T> {
  key: keyof T; // Field key, sort by data field
  label?: string; // Column header label
  renderCell?: (row: T, rowIndex: number) => React.ReactNode; // Cell renderer
  renderHeader?: () => React.ReactNode;
  sortable?: boolean; // Is the column sortable
  isDate?: boolean; // Is this a date column
  footer?: string | React.ReactNode;
  classNameCell?: string;
}

export interface ITableTheme {
  hideHeaders?: boolean;
  showTableBorder?: boolean;
  tableWidth?: string;
  tableGridLayout?: string;
}

interface TableWrapperProps<T> {
  data: T[]; // Data array
  columns: ColumnConfig<T>[]; // Column configurations
  onRowClick?: (row: T, event: React.SyntheticEvent) => void;
  isLoading?: boolean; // Show loading state
  isEmpty?: boolean; // Show empty state
  noDataIndication?:
    | JSX.Element
    | {
        heading?: string;
        subText?: string;
      };
  idField?: keyof T; // Field to use as row identifier
  defaultSortKey?: keyof T;
  reverseDefaultSortOrder?: boolean;
  theme?: ITableTheme;
  customTheme?: Theme;
  rowStyles?: (row: T) => Record<string, unknown>;
  rowExpand?: {
    renderer: (row: T) => React.ReactNode;
    className?: string;
    columnRenderer?: (expanded: boolean) => JSX.Element;
    position?: 'left' | 'right';
    allowMultiExpand?: boolean;
  };
  rowSelect?: {
    state: Array<T & { id: string }>;
    onChange?: (selectedRows: Array<T & { id: string }>) => void;
  };
  id?: string;
  testId?: string;
}

const TableWrapper = <T,>({
  data,
  columns,
  onRowClick,
  isLoading = false,
  isEmpty = false,
  idField = 'id' as keyof T,
  defaultSortKey,
  reverseDefaultSortOrder = true, // since most default keys are date fields that need to be sorted in desc order
  theme = {
    hideHeaders: false,
  },
  customTheme = {},
  rowExpand,
  noDataIndication = {},
  id,
  testId,
  rowStyles,
  rowSelect,
}: TableWrapperProps<T>): JSX.Element => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const enableRowSelect = rowSelect;

  const toggleRowExpand = (rowId: string) => {
    setExpandedRows((prevExpandedRows) => {
      if (rowExpand?.allowMultiExpand === false) {
        // For single row expansion
        return prevExpandedRows.includes(rowId) ? [] : [rowId];
      } else {
        if (prevExpandedRows.includes(rowId)) {
          return prevExpandedRows.filter((_id) => _id !== rowId);
        }
        return [...prevExpandedRows, rowId];
      }
    });
  };

  const generateTheme = (themeOptions?: ITableTheme, _customTheme?: Theme) => {
    const themeStyles: Theme = {
      HeaderRow: '',
      Row: `
        &:last-child {
          & > td {
            border-bottom: 1px solid #e5e7e9;
          } 
        }
      `,
      Table: '',
      HeaderCell: `
        & .active {
          font-weight: 500 !important;
        }
      `,
      FooterCell: `border-bottom: 1px solid #e5e7e9;`,
    };

    if (themeOptions?.hideHeaders) {
      themeStyles.HeaderRow += `display: none;`;
      themeStyles.Row += `
        &:first-child > td {
          border-top: none;
        }
      `;
    }
    if (themeOptions?.showTableBorder) {
      themeStyles.Table += `
      border: 1px solid #e5e7e9;
      border-bottom-width: 0px;
    `;
    } else {
      themeStyles.Table += `
      border: none;
    `;
    }
    if (themeOptions?.tableWidth) {
      themeStyles.Table += `
        width: ${themeOptions?.tableWidth};
      `;
    }
    if (themeOptions?.tableGridLayout) {
      themeStyles.Table += `
        grid-template-columns: ${themeOptions?.tableGridLayout};
      `;
    }
    if (onRowClick || rowExpand) {
      themeStyles.Row += `
        cursor: pointer;
        &:hover {
          background-color: #f7fafe;
        } 
      `;
    }

    const combinedTheme = { ...themeStyles };
    if (_customTheme) {
      Object.keys(_customTheme).forEach((key) => {
        const styleKey = key as keyof Theme;
        combinedTheme[styleKey] = [
          themeStyles[styleKey] || '',
          _customTheme[styleKey] || '',
        ].join(' ');
      });
    }
    return combinedTheme;
  };

  // Map data to ensure each row has an `id`
  const tableData = {
    nodes: data.map((item, index) => ({
      ...item,
      id: item[idField] ? String(item[idField]) : index.toString(),
    })),
  };

  const select = useRowSelect(
    tableData,
    {
      state: { ids: rowSelect?.state?.map((node) => node.id) || [] },
      onChange: (_data, state) => {
        if (rowSelect) {
          const selectedItems = state.ids.map((_id: string) =>
            tableData.nodes.find((node) => String(node.id) === _id)
          );
          rowSelect.onChange?.(selectedItems);
        }
      },
    },
    {
      isPartialToAll: true,
      clickType: SelectClickTypes.ButtonClick,
    }
  );

  const themeConfig = useTheme(generateTheme(theme, customTheme));

  const sortFns: Record<keyof T, SortFn> = columns.reduce((acc, column) => {
    if (column.sortable) {
      acc[column.key] = (array) => {
        const _sortByField = column.key as string;
        const firstItem = array[0]?.[_sortByField];

        // Normalize values to ensure nulls are handled consistently
        const normalize = (value: unknown): string | number => {
          if (value === null || value === undefined) {
            return ''; // Treat null/undefined as empty string for strings
          }
          return value as string | number; // Cast after null check
        };

        if (column.isDate) {
          return [...array].sort((a, b) => {
            const dateA = new Date(
              normalize(a[_sortByField]) as string
            ).getTime();
            const dateB = new Date(
              normalize(b[_sortByField]) as string
            ).getTime();
            return dateA - dateB;
          });
        } else if (
          typeof firstItem === 'number' ||
          typeof firstItem === 'string'
        ) {
          return [...array].sort((a, b) => {
            const valueA = normalize(a[_sortByField]);
            const valueB = normalize(b[_sortByField]);

            if (typeof valueA === 'number' && typeof valueB === 'number') {
              return valueA - valueB; // Numeric comparison
            }

            return String(valueA).localeCompare(String(valueB)); // String comparison
          });
        }

        return array; // Return unsorted array if type is not supported
      };
    }
    return acc;
  }, {} as Record<keyof T, SortFn>);

  const sort = useSort<T & { id: string }>(
    tableData,
    defaultSortKey
      ? {
          state: {
            sortKey: String(defaultSortKey),
            reverse: reverseDefaultSortOrder,
          },
        }
      : {},
    {
      sortFns,
      sortIcon: {
        margin: '0px',
        iconDefault: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            fill="#ccc"
            viewBox="0 0 256 256"
          >
            <path d="M72.61,83.06a8,8,0,0,1,1.73-8.72l48-48a8,8,0,0,1,11.32,0l48,48A8,8,0,0,1,176,88H80A8,8,0,0,1,72.61,83.06ZM176,168H80a8,8,0,0,0-5.66,13.66l48,48a8,8,0,0,0,11.32,0l48-48A8,8,0,0,0,176,168Z"></path>
          </svg>
        ),
        iconUp: (
          <Icon
            name="chevron-up"
            size={16}
            className="table-sort-icon-up"
            aria-hidden="true"
          />
        ),
        iconDown: (
          <Icon
            name="chevron-down"
            size={16}
            className="table-sort-icon-down"
            aria-hidden="true"
          />
        ),
      },
    }
  );

  if (isLoading) {
    return <></>;
  }

  if (isEmpty || data?.length === 0) {
    return (
      <div className="table-text">
        {React.isValidElement(noDataIndication) ? (
          noDataIndication
        ) : (
          <NoDataAvailable
            heading={
              'heading' in noDataIndication ? noDataIndication?.heading : ''
            }
            subText={
              'subText' in noDataIndication ? noDataIndication?.subText : ''
            }
          />
        )}
      </div>
    );
  }

  return (
    <BaseTable
      data={tableData}
      sort={sort}
      theme={themeConfig}
      id={id}
      data-testid={testId}
      select={select}
    >
      {(tableList: Array<T & { id: string }>) => {
        return (
          <>
            <Header>
              <HeaderRow>
                {enableRowSelect && <HeaderCellSelect />}
                {rowExpand?.columnRenderer &&
                  rowExpand?.position === 'left' && (
                    <HeaderCell key="expand-col" />
                  )}
                {columns.map((column) =>
                  column.sortable ? (
                    <HeaderCellSort
                      key={String(column.key)}
                      sortKey={String(column.key)}
                    >
                      {column?.renderHeader?.() || column.label || ''}
                    </HeaderCellSort>
                  ) : (
                    <HeaderCell key={String(column.key)}>
                      {column?.renderHeader?.() || column.label || ''}
                    </HeaderCell>
                  )
                )}
                {rowExpand?.columnRenderer &&
                  (rowExpand?.position === 'right' || !rowExpand?.position) && (
                    <HeaderCell key="expand-col" />
                  )}
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map((row, rowIndex) => {
                const isRowExpanded = expandedRows.includes(row.id);

                return (
                  <React.Fragment key={row.id}>
                    <Row
                      key={row.id}
                      item={row}
                      onClick={
                        onRowClick
                          ? onRowClick
                          : toggleRowExpand
                          ? () => toggleRowExpand(row.id)
                          : undefined
                      }
                      style={rowStyles?.(row)}
                    >
                      {enableRowSelect && <CellSelect item={row} />}
                      {rowExpand?.columnRenderer &&
                        rowExpand?.position === 'left' && (
                          <Cell key={`${row.id}-expand`}>
                            {rowExpand?.columnRenderer(isRowExpanded)}
                          </Cell>
                        )}
                      {columns.map((column) => (
                        <Cell
                          key={`${row.id}-${String(column.key)}`}
                          className={column.classNameCell}
                        >
                          {column.renderCell
                            ? column.renderCell(row, rowIndex)
                            : String(row[column.key] || '')}
                        </Cell>
                      ))}
                      {rowExpand?.columnRenderer &&
                        (rowExpand?.position === 'right' ||
                          !rowExpand?.position) && (
                          <Cell key={`${row.id}-expand`}>
                            {rowExpand?.columnRenderer(isRowExpanded)}
                          </Cell>
                        )}
                    </Row>
                    {isRowExpanded && rowExpand && rowExpand?.renderer && (
                      <tr
                        style={{
                          display: 'flex',
                          gridColumn: '1 / -1',
                        }}
                      >
                        <td
                          style={{ flex: '1' }}
                          colSpan={columns.length}
                          className={classNames(
                            rowExpand.className,
                            'row-expansion-style'
                          )}
                        >
                          {rowExpand.renderer(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Body>
            {columns?.some((c) => c.footer) && (
              <Footer>
                <FooterRow>
                  {columns.map(({ footer }) => (
                    <FooterCell>{footer || ''}</FooterCell>
                  ))}
                </FooterRow>
              </Footer>
            )}
          </>
        );
      }}
    </BaseTable>
  );
};

export default TableWrapper;
